// Configuration des préférences de recherche utilisateur
// Basé sur le système de préférences d'Open Food Facts

export const PREFERENCE_LEVELS = {
  NOT_IMPORTANT: 'not_important',
  IMPORTANT: 'important',
  VERY_IMPORTANT: 'very_important',
  MANDATORY: 'mandatory',
};

// config/searchPreferences.js

export const DEFAULT_PREFERENCES = {
  // Qualité nutritionnelle
  nutritional_quality: {
    nutriScore: PREFERENCE_LEVELS.NOT_IMPORTANT,
    salt: PREFERENCE_LEVELS.NOT_IMPORTANT,
    sugars: PREFERENCE_LEVELS.NOT_IMPORTANT,
    fat: PREFERENCE_LEVELS.NOT_IMPORTANT,
    saturatedFat: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },

  // Transformation des aliments
  food_processing: {
    nova: PREFERENCE_LEVELS.NOT_IMPORTANT,
    additives: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },

  // Allergènes
  allergens: {
    gluten: PREFERENCE_LEVELS.NOT_IMPORTANT,
    milk: PREFERENCE_LEVELS.NOT_IMPORTANT,
    eggs: PREFERENCE_LEVELS.NOT_IMPORTANT,
    tree_nuts: PREFERENCE_LEVELS.NOT_IMPORTANT,
    peanuts: PREFERENCE_LEVELS.NOT_IMPORTANT,
    sesame: PREFERENCE_LEVELS.NOT_IMPORTANT,
    soy: PREFERENCE_LEVELS.NOT_IMPORTANT,
    celery: PREFERENCE_LEVELS.NOT_IMPORTANT,
    mustard: PREFERENCE_LEVELS.NOT_IMPORTANT,
    lupin: PREFERENCE_LEVELS.NOT_IMPORTANT,
    fish: PREFERENCE_LEVELS.NOT_IMPORTANT,
    crustaceans: PREFERENCE_LEVELS.NOT_IMPORTANT,
    mollusks: PREFERENCE_LEVELS.NOT_IMPORTANT,
    sulfites: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },

  // Ingrédients
  ingredients: {
    vegan: PREFERENCE_LEVELS.NOT_IMPORTANT,
    vegetarian: PREFERENCE_LEVELS.NOT_IMPORTANT,
    palmOilFree: PREFERENCE_LEVELS.NOT_IMPORTANT,
    undesirableIngredients: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },

  // Labels
  labels: {
    organic: PREFERENCE_LEVELS.NOT_IMPORTANT,
    fairTrade: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },

  // Environnement
  environment: {
    greenScore: PREFERENCE_LEVELS.NOT_IMPORTANT,
    deforestation: PREFERENCE_LEVELS.NOT_IMPORTANT,
  },
};

export const PREFERENCE_CATEGORIES = {
  NUTRITIONAL_QUALITY: 'nutritional_quality',
  FOOD_PROCESSING: 'food_processing',
  ALLERGENS: 'allergens',
  INGREDIENTS: 'ingredients',
  LABELS: 'labels',
  ENVIRONMENT: 'environment',
};

// Mapping pour les affichages
export const PREFERENCE_DISPLAY = {
  nutritional_quality: {
    title: 'Qualité nutritionnelle',
    fields: {
      nutriScore: 'Bonne qualité nutritionnelle (Nutri-Score)',
      salt: 'Sel en faible quantité',
      sugars: 'Sucres en faible quantité',
      fat: 'Matières grasses en faible quantité',
      saturatedFat: 'Acides gras saturés en faible quantité',
    },
  },
  food_processing: {
    title: 'Transformation des aliments',
    fields: {
      nova: 'Pas ou peu de transformation des aliments (groupe NOVA)',
      additives: 'Pas ou peu d\'additifs',
    },
  },
  allergens: {
    title: 'Allergènes',
    fields: {
      gluten: 'Sans Gluten',
      milk: 'Sans Lait',
      eggs: 'Sans Œufs',
      tree_nuts: 'Sans Fruits à coque',
      peanuts: 'Sans Arachides',
      sesame: 'Sans Graines de sésame',
      soy: 'Sans Soja',
      celery: 'Sans Céleri',
      mustard: 'Sans Moutarde',
      lupin: 'Sans Lupin',
      fish: 'Sans Poisson',
      crustaceans: 'Sans Crustacés',
      mollusks: 'Sans Mollusques',
      sulfites: 'Sans Anhydride sulfureux et sulfites',
    },
  },
  ingredients: {
    title: 'Ingrédients',
    fields: {
      vegan: 'Végétalien',
      vegetarian: 'Végétarien',
      palmOilFree: 'Sans huile de palme',
      undesirableIngredients: 'Ingrédients indésirables',
    },
  },
  labels: {
    title: 'Labels',
    fields: {
      organic: 'Agriculture biologique',
      fairTrade: 'Commerce équitable',
    },
  },
  environment: {
    title: 'Environnement',
    fields: {
      greenScore: 'Faible impact environnemental (Green-Score)',
      deforestation: 'Faible risque de déforestation (Empreinte forêt)',
    },
  },
};

export const LEVEL_LABELS = {
  not_important: 'Pas important',
  important: 'Important',
  very_important: 'Très important',
  mandatory: 'Obligatoire',
};

// Utilitaires
export const loadPreferences = () => {
  try {
    const stored = localStorage.getItem('searchPreferences');
    return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES;
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const savePreferences = (preferences) => {
  try {
    localStorage.setItem('searchPreferences', JSON.stringify(preferences));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des préférences:', error);
  }
};

export const resetPreferences = () => {
  localStorage.removeItem('searchPreferences');
  return DEFAULT_PREFERENCES;
};

// Score un produit basé sur les préférences
export const scoreProductByPreferences = (product, preferences) => {
  let score = 0;
  let maxScore = 0;

  // Nutri-Score
  if (preferences.nutritional_quality.nutriScore !== PREFERENCE_LEVELS.NOT_IMPORTANT) {
    maxScore += 100;
    const nutriScore = product.nutriscore_grade?.toLowerCase();
    const scoreMap = { a: 100, b: 75, c: 50, d: 25, e: 0 };
    if (nutriScore && scoreMap[nutriScore] !== undefined) {
      score += scoreMap[nutriScore];
    }
  }

  // Éco-Score / Green Score
  if (preferences.environment.greenScore !== PREFERENCE_LEVELS.NOT_IMPORTANT) {
    maxScore += 100;
    const ecoScore = product.ecoscore_grade?.toLowerCase();
    const scoreMap = { a: 100, b: 75, c: 50, d: 25, e: 0 };
    if (ecoScore && scoreMap[ecoScore] !== undefined) {
      score += scoreMap[ecoScore];
    }
  }

  // Labels
  const labels = product.labels_tags || [];
  if (preferences.labels.organic !== PREFERENCE_LEVELS.NOT_IMPORTANT) {
    maxScore += 50;
    if (labels.some(l => l.includes('organic'))) {
      score += 50;
    }
  }

  return maxScore > 0 ? (score / maxScore) * 100 : 50;
};
